import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik, type FormikHelpers } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import Result from 'components/v2/common/Result';

import api from 'api';
import { AgentRoles } from 'api/models/payload/AgentRoles';
import {
    CASE_RETAINER_TYPE_LABEL_KEYS,
    getCreateRetainerFields,
    isCaseRetainerType,
} from 'helpers/applicationRetainers';
import useTranslate from 'hooks/useTranslate';
import { loadAgents } from 'redux/agents/agents.middleware';
import { setAlert } from 'redux/alert';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { ApplicationRetainersList, CreateCaseRetainerInterface } from 'types';
import { getErrorMessage, getErrorStatus } from 'utils/v2/errorHelper';
import {
    createCaseRetainerDefaultValues,
    getCreateCaseRetainerValidationSchema,
} from 'utils/validations/applicationRetainers';

interface CreateRetainerDialogProps {
    applicationId: number;
    list: ApplicationRetainersList;
    onClose: () => void;
    onCreated: () => void;
    /** 409 / 404: the list changed under us — caller refetches. */
    onStale: () => void;
}

interface TemplateOption {
    label: string;
    value: string;
}

const CreateRetainerFormDialog: React.FC<CreateRetainerDialogProps> = ({
    applicationId,
    list,
    onClose,
    onCreated,
    onStale,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const agents = useSelector((state: RootState) => state.agents.list);
    const agentsLoaded = useSelector((state: RootState) => state.agents.isLoaded);

    const fields = getCreateRetainerFields({ flow: list.flow, allowedTypes: list.allowed_types });
    const [selectedType, setSelectedType] = useState<string>(fields.lockedType ?? '');
    const [templates, setTemplates] = useState<TemplateOption[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    useEffect(() => {
        if (fields.showAgent && !agentsLoaded) {
            void dispatch(loadAgents());
        }
    }, [agentsLoaded, dispatch, fields.showAgent]);

    // Only published templates of the chosen type are offered
    useEffect(() => {
        if (!isCaseRetainerType(selectedType)) {
            setTemplates([]);
            return undefined;
        }

        let cancelled = false;
        setTemplatesLoading(true);
        api.retainerTemplates.utility
            .getForSelection({ type: selectedType })
            .then((response) => {
                if (cancelled) return;
                const rows = response.data?.data ?? [];
                setTemplates(
                    rows
                        .filter((template) => template.status === 'published')
                        .map((template) => ({ label: template.name, value: String(template.id) }))
                );
            })
            .catch((error) => {
                if (cancelled) return;
                setTemplates([]);
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            })
            .finally(() => {
                if (!cancelled) setTemplatesLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [dispatch, selectedType, translate]);

    const typeOptions = useMemo(
        () =>
            list.allowed_types.filter(isCaseRetainerType).map((type) => ({
                label: translate(CASE_RETAINER_TYPE_LABEL_KEYS[type]),
                value: type,
            })),
        [list.allowed_types, translate]
    );

    const agentOptions = useMemo(
        () => agents.map((agent) => ({ label: agent.label, value: agent.value })),
        [agents]
    );

    const initialValues: CreateCaseRetainerInterface = {
        ...createCaseRetainerDefaultValues,
        agreementType: fields.lockedType ?? '',
    };

    const handleSubmit = async (
        values: CreateCaseRetainerInterface,
        { setFieldError }: FormikHelpers<CreateCaseRetainerInterface>
    ) => {
        const templateId = Number(values.templateId);
        if (!templateId) return;

        try {
            await api.applicationRetainers.create(applicationId, {
                template_id: templateId,
                signing_blocked: values.signingBlocked,
            });
        } catch (error) {
            const status = getErrorStatus(error);
            const message = getErrorMessage(error, translate('somethingWentWrong'));
            if (status === 400) {
                // Validation failures (no offer, wrong type) are shown verbatim on the form
                setFieldError('templateId', message);
                return;
            }
            dispatch(setAlert({ message, type: 'error' }));
            if (status === 409 || status === 404) {
                onStale();
            }
            return;
        }

        if (fields.showAgent && values.agentId) {
            try {
                await api.agents.assignAgent(AgentRoles.CONSULTANT, {
                    application_id: applicationId,
                    agent_id: Number(values.agentId),
                });
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('retainerAgentAssignFailed')),
                        type: 'warning',
                    })
                );
                onCreated();
                return;
            }
        }

        dispatch(setAlert({ message: translate('retainerCreated'), type: 'success' }));
        onCreated();
    };

    const noAllowedTypes = list.allowed_types.length === 0;
    const noTemplates = !templatesLoading && Boolean(selectedType) && templates.length === 0;

    return (
        <Dialog open fullWidth maxWidth="sm" fullScreen={isMobile} scroll="paper">
            <Formik
                initialValues={initialValues}
                validationSchema={getCreateCaseRetainerValidationSchema({
                    requireType: fields.showType,
                    requireAgent: fields.showAgent,
                })}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, setFieldValue }) => (
                    <Form>
                        <DialogTitle>
                            {translate(
                                fields.lockedType === 'visa-application'
                                    ? 'createVisaRetainer'
                                    : 'createRetainer'
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {translate('createRetainerDescription')}
                            </Typography>
                        </DialogTitle>
                        <DialogContent dividers>
                            {noAllowedTypes ? (
                                <Result
                                    title={translate('noAllowedRetainerTypes')}
                                    description={translate('noAllowedRetainerTypesDescription')}
                                />
                            ) : (
                                <FormGridLayout sx={{ gridTemplateColumns: '1fr' }}>
                                    {fields.showType ? (
                                        <FormField
                                            name="agreementType"
                                            label="selectRetainerType"
                                            type="select"
                                            options={typeOptions}
                                            disabled={isSubmitting}
                                            onChange={(value) => {
                                                setSelectedType(String(value ?? ''));
                                                void setFieldValue('templateId', '');
                                            }}
                                        />
                                    ) : null}
                                    <FormField
                                        name="templateId"
                                        label="selectRetainerTemplate"
                                        type="select"
                                        options={templates}
                                        disabled={isSubmitting || templatesLoading || !selectedType}
                                    />
                                    <FormField
                                        name="signingBlocked"
                                        label="blockRetainerSigning"
                                        type="checkbox"
                                        description="blockRetainerSigningDescription"
                                        disabled={isSubmitting}
                                    />
                                    {noTemplates ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {translate('noPublishedTemplatesForType')}
                                        </Typography>
                                    ) : null}
                                    {fields.showAgent ? (
                                        <FormField
                                            name="agentId"
                                            label="selectRetainerAgent"
                                            type="select"
                                            options={agentOptions}
                                            disabled={isSubmitting}
                                        />
                                    ) : null}
                                </FormGridLayout>
                            )}
                        </DialogContent>
                        <DialogActions
                            sx={{
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1.5, sm: 2 },
                                gap: 1,
                                flexDirection: { xs: 'column-reverse', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                '& > .MuiButton-root, & > button': {
                                    width: { xs: '100%', sm: 'auto' },
                                    m: '0 !important',
                                },
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                disabled={isSubmitting}
                                sx={{ textTransform: 'none' }}
                            >
                                {translate('cancel')}
                            </Button>
                            <SubmitButton
                                loading={isSubmitting}
                                disabled={noAllowedTypes || noTemplates}
                                fullWidth={isMobile}
                            >
                                {fields.lockedType === 'visa-application'
                                    ? 'createVisaRetainer'
                                    : 'createRetainer'}
                            </SubmitButton>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export const CreateRetainerDialog: React.FC<CreateRetainerDialogProps> = (props) => (
    <ThemeProvider theme={appThemeV2}>
        <CreateRetainerFormDialog {...props} />
    </ThemeProvider>
);
